import { useState } from "react";
import { Check, Copy, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";

type FunnelTag = {
  readonly key: string;
  readonly tag: string;
  readonly stage: string;
  readonly description: string;
  readonly important?: boolean;
};

const FUNNEL_TAGS: ReadonlyArray<FunnelTag> = [
  {
    key: "qualifiedLead",
    tag: "kwalifikowany-lead",
    stage: "Etap 1 — Lead",
    description: "Lead spelnia kryteria ICP (idealnego klienta). Dodaj po wstepnej kwalifikacji.",
  },
  {
    key: "unqualifiedLead",
    tag: "niekwalifikowany-lead",
    stage: "Etap 1 — Lead",
    description: "Lead nie spelnia kryteriow (np. brak budzetu, zla branza). Dodaj zeby filtrowac.",
  },
  {
    key: "callNoShow",
    tag: "rozmowa-nie-odbyta",
    stage: "Etap 2 — Rozmowa",
    description: "Lead umowil rozmowe ale sie nie pojawil (no-show). Liczone do show rate.",
  },
  {
    key: "callQualified",
    tag: "rozmowa-kwalifikowana",
    stage: "Etap 2 — Rozmowa",
    description: "Rozmowa sie odbyla i klient nadal jest dobrym kandydatem.",
  },
  {
    key: "callUnqualified",
    tag: "rozmowa-niekwalifikowana",
    stage: "Etap 2 — Rozmowa",
    description: "Rozmowa sie odbyla, ale po niej klient zostal odrzucony.",
  },
  {
    key: "sale",
    tag: "sprzedaz",
    stage: "Etap 3 — Sprzedaz",
    description:
      "KLUCZOWY tag. Po dodaniu konwersja jest przypisana do reklamy w dashboardzie. Bez tego nie zobaczysz ROAS.",
    important: true,
  },
  {
    key: "lost",
    tag: "przegrana",
    stage: "Etap 3 — Sprzedaz",
    description:
      'Klient ostatecznie nie kupil (np. wybral konkurencje). Razem ze "sprzedaz" liczy close rate.',
  },
];

const CUSTOM_FIELD = {
  key: "data_sprzedazy",
  description:
    "Custom field w GHL (typ: Date). Ustaw na dzien zamkniecia sprzedazy — uzywany do atrybucji w dashboardzie zamiast updatedAt opportunity.",
};

function CopyableTag({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // noop — clipboard API not available
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="group inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 font-mono text-[11px] text-white/90 hover:bg-white/10 hover:border-white/20 transition-colors"
      type="button"
      title="Skopiuj tag"
    >
      <span>{value}</span>
      {copied ? (
        <Check className="h-3 w-3 text-emerald-300" />
      ) : (
        <Copy className="h-3 w-3 text-white/40 group-hover:text-white/70" />
      )}
    </button>
  );
}

export function GhlTagsGuidePanel() {
  const { t: _ } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const grouped = FUNNEL_TAGS.reduce<Record<string, FunnelTag[]>>((acc, t) => {
    (acc[t.stage] ||= []).push(t);
    return acc;
  }, {});

  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
      <button onClick={() => setExpanded((v) => !v)} className="w-full text-left" type="button">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <Tag className="h-4 w-4 text-amber-300" />
              Jakie tagi GHL ustawic zeby tracking dzialal
            </div>
            <p className="text-xs text-white/60 mt-1">
              Dodaj te tagi do kontaktow w GHL w odpowiednich momentach — wtedy dashboard pokaze
              pelny funel, atrybucje sprzedazy do reklam i ROAS.{" "}
              <span className="text-amber-300/80">Bez tagu "sprzedaz" nie zobaczysz ROAS.</span>
            </p>
          </div>
          <span className="shrink-0 text-[11px] uppercase tracking-widest text-white/40">
            {expanded ? "Zwin" : "Rozwin"}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="space-y-4 pt-2 border-t border-white/[0.06]">
          {Object.entries(grouped).map(([stage, tags]) => (
            <div key={stage} className="space-y-2">
              <div className="text-[11px] uppercase tracking-widest text-white/40">{stage}</div>
              <div className="space-y-2">
                {tags.map((ft) => (
                  <div
                    key={ft.key}
                    className={`flex flex-col gap-1.5 rounded-md border p-2.5 ${
                      ft.important
                        ? "border-amber-400/30 bg-amber-400/[0.05]"
                        : "border-white/[0.06] bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <CopyableTag value={ft.tag} />
                      {ft.important && (
                        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                          wymagane
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/60 leading-relaxed">{ft.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-widest text-white/40">Custom field</div>
            <div className="flex flex-col gap-1.5 rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5">
              <CopyableTag value={CUSTOM_FIELD.key} />
              <p className="text-xs text-white/60 leading-relaxed">{CUSTOM_FIELD.description}</p>
            </div>
          </div>

          <div className="rounded-md border border-sky-400/20 bg-sky-400/[0.05] p-3">
            <p className="text-xs text-white/70 leading-relaxed">
              <strong className="text-sky-200">Tip:</strong> w GHL Workflow ustaw automatyczne
              dodawanie tagow na podstawie statusu opportunity (np. opportunity moved to "Won" → add
              tag <span className="font-mono text-amber-300">sprzedaz</span>). Wtedy nie musisz
              tagowac recznie.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
